/**
 * sessionKind — the FE half of `vc_be/utils/sessionKind.js`.
 *
 * This folder serves TWO products: Demo Classes and Webinars. They are the
 * same screens over the same API shape — a public link, an anonymous
 * registration, a lead list, a lobby, a live room, a follow-up form — so every
 * page here takes a `kind` prop and reads its words, routes and endpoints from
 * this file. Nothing below may hardcode "demo" in a visible string again.
 *
 * ⚠️ The directory keeps its `DemoClasses/` name and its `demo-*` class
 * prefixes deliberately. Ten files import `demo-classes.scss`, and
 * `Instructors/InstructorProfilePage` renders `demo-classes-page__table` from a
 * different module entirely — renaming the folder would be a large, purely
 * cosmetic diff across code nobody is otherwise touching.
 *
 * Keep in sync with the backend file: `publicPath` must match, or a
 * registration email links to a page that does not exist.
 */

export const SESSION_KINDS = {
  demo: {
    kind: "demo",

    /** Lowercase noun for sentences: "This demo has ended." */
    noun: "demo",
    /** Title-case, for buttons and headings: "Start Demo". */
    Noun: "Demo",
    nounPlural: "demos",
    /** Full product name: "demo class". */
    fullNoun: "demo class",
    FullNoun: "Demo class",
    /** Module name, for the page header and the sidebar. */
    moduleTitle: "Demo Classes",
    insightsTitle: "Demo Classes Insights",

    /** Admin route base — `/demo-classes/:id`. */
    adminBase: "/demo-classes",
    /** Instructor-scoped route base — `/i/demo-classes/:id`. */
    instructorBase: "/i/demo-classes",
    /** Public route base — `/demo/:slug`. */
    publicBase: "/demo",

    /** apiClient path prefix for the admin endpoints. */
    apiBase: "/admin/demo",
    /** Path prefix for the anonymous endpoints (no auth interceptor). */
    publicApiBase: "/public/demo",

    /** Plan feature that unlocks the module. */
    planFeature: "demoClasses",
    /** Storage key namespace for the attendee's guest token. */
    guestStorePrefix: "demo-guest",

    copy: {
      listDescription:
        "Run public demo sessions for prospects. Share a link, capture leads, convert them into students.",
      formDescription:
        "Define a public demo session. The link can be shared on social media or embedded on your website.",
      emptyTitle: "No demo sessions yet",
      emptyDescription:
        "Create your first demo to start collecting leads from interested prospects.",
      insightsDescription: "Track funnel performance across recent demo sessions.",
      heroTag: "Free live demo",
      heroTitle: "Live, hands-on demo class",
      heroSubtitle:
        "Experience the teaching style, ask questions, and decide if it's the right fit — all from the comfort of your home.",
      heroFeatures: [
        "Live interaction with the instructor",
        "Hands-on demo + Q&A",
        "Recording shared after the session",
      ],
      shellLabel: "Live demo class",
      hostedLabel: "Hosted demo session",
      landingSubtitle:
        "Get a real feel for the course in this free live session — meet the instructor, ask questions, and decide if it's right for you.",
      registerTitle: "Reserve your seat",
      registerSubtitle:
        "We'll text and email you the join link a few minutes before the demo starts.",
      consentDefault: "I agree to be contacted about this course.",
      titlePlaceholder: "e.g. Intro to Python — Free Demo",
    },
  },

  webinar: {
    kind: "webinar",

    noun: "webinar",
    Noun: "Webinar",
    nounPlural: "webinars",
    fullNoun: "webinar",
    FullNoun: "Webinar",
    moduleTitle: "Webinars",
    insightsTitle: "Webinar Insights",

    adminBase: "/webinars",
    instructorBase: "/i/webinars",
    publicBase: "/webinar",

    apiBase: "/admin/webinars",
    publicApiBase: "/public/webinar",

    planFeature: "webinars",
    guestStorePrefix: "webinar-guest",

    copy: {
      listDescription:
        "Run public webinars for a wider audience. Share a link, capture registrations, follow up with everyone who attended.",
      formDescription:
        "Define a public webinar. The link can be shared on social media, in ads or embedded on your website.",
      emptyTitle: "No webinars yet",
      emptyDescription:
        "Create your first webinar to start building an audience and collecting registrations.",
      insightsDescription: "Track registration and attendance across recent webinars.",
      heroTag: "Free live webinar",
      heroTitle: "Live, expert-led webinar",
      heroSubtitle:
        "Join the session live, ask your questions in the room, and take the recording away with you.",
      heroFeatures: [
        "Live session with a subject expert",
        "Open Q&A at the end",
        "Recording shared after the session",
      ],
      shellLabel: "Live webinar",
      hostedLabel: "Hosted webinar",
      landingSubtitle:
        "Save your seat for this free live webinar — hear from the speaker, ask questions, and get the recording afterwards.",
      registerTitle: "Save your seat",
      registerSubtitle:
        "We'll text and email you the join link a few minutes before the webinar starts.",
      consentDefault: "I agree to be contacted about this webinar and related courses.",
      titlePlaceholder: "e.g. Careers in Data Science — Free Webinar",
    },
  },
};

/** Anything that is not the literal "webinar" is a demo — matches the backend. */
export const resolveSessionKind = (kind) =>
  SESSION_KINDS[kind === "webinar" ? "webinar" : "demo"];

export default SESSION_KINDS;
